import { User, GitBranch } from 'lucide-react'
import { useROIStore } from '../store'
import { PROVIDERS } from '../data'
import { EmptyState } from '@components/ui/EmptyState'

export function FlowVisualization() {
  const { regions, selectedFlowRegionId, setSelectedFlowRegion, getRegionCalculation } = useROIStore()

  const selectedRegion = regions.find((r) => r.id === selectedFlowRegionId) ?? regions[0]
  if (!selectedRegion) return null

  const calculation = getRegionCalculation(selectedRegion.id)
  const provider = PROVIDERS.find((p) => p.id === selectedRegion.provider)

  // Calculate cascade steps with cumulative pass rates
  const steps: Array<{
    name: string
    benchmarkRate: number
    incrementalLift: number
    remainingFailBefore: number
    remainingFailAfter: number
    cumulativePass: number
  }> = []

  let currentPassRate = selectedRegion.passRate
  let failRate = 100 - selectedRegion.passRate

  selectedRegion.stack.forEach((item) => {
    const failBefore = failRate
    const additionalPasses = failRate * (item.incrementalLift / 100)
    currentPassRate += additionalPasses
    failRate -= additionalPasses

    steps.push({
      name: item.description,
      benchmarkRate: item.passRate,
      incrementalLift: item.incrementalLift,
      remainingFailBefore: failBefore,
      remainingFailAfter: failRate,
      cumulativePass: currentPassRate,
    })
  })

  const finalPassRate = calculation?.newRate ?? selectedRegion.passRate
  const finalFailRate = 100 - finalPassRate

  // Helper for ordinal suffix
  const getOrdinal = (n: number) => {
    if (n === 1) return '1st'
    if (n === 2) return '2nd'
    if (n === 3) return '3rd'
    return `${n}th`
  }

  return (
    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-[var(--text-primary)]">
          Verification Flow
        </h3>

        {/* Region selector for multi-region */}
        {regions.length > 1 && (
          <select
            className="bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded px-2 py-1 text-xs text-[var(--text-primary)]"
            value={selectedFlowRegionId ?? ''}
            onChange={(e) => setSelectedFlowRegion(Number(e.target.value))}
          >
            {regions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name || r.country || `Region ${r.id}`}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Flow Comparison - Before 30%, After 70% */}
      <div className="grid grid-cols-[0.3fr_auto_0.7fr] gap-4">
        {/* BEFORE Section */}
        <div className="space-y-3">
          <div className="text-center border-b border-[var(--border-color)] pb-3">
            <div className="text-sm font-semibold text-[var(--text-muted)] uppercase tracking-wide">Before</div>
            <div className="text-xs text-[var(--text-muted)]">{provider?.name ?? 'Current Provider'}</div>
          </div>

          {/* Flow diagram - Before */}
          <div className="bg-[var(--bg-tertiary)] rounded-lg p-5">
            {/* Customer node - aligned left to match FrankieOne */}
            <div className="flex">
              <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 py-2 text-sm text-[var(--text-primary)] flex items-center gap-2">
                <User size={16} />
                Customer
              </div>
            </div>

            {/* Vertical connector line - same as FrankieOne */}
            <div className="flex">
              <div className="w-0.5 h-6 bg-gray-600 ml-6" />
            </div>

            {/* Provider check */}
            <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-3 text-center">
              <div className="text-xs text-[var(--text-secondary)] mb-1">Single Provider Check</div>
              <div className="text-sm font-medium text-[var(--text-primary)]">{provider?.name ?? 'Provider'}</div>
            </div>

            {/* Match rate - styled like FrankieOne */}
            <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)] mt-3 justify-center">
              <span>→</span>
              <span>Match Rate {selectedRegion.passRate.toFixed(0)}%</span>
            </div>

            {/* Vertical connector line */}
            <div className="flex justify-center">
              <div className="w-0.5 h-4 bg-gray-600" />
            </div>

            {/* Partial / No Match badge - styled like FrankieOne */}
            <div className="flex justify-center mt-2">
              <div className="inline-flex items-center gap-2 bg-danger/10 border border-danger/30 rounded px-3 py-1.5">
                <span className="text-xs text-danger/80">Partial / No Match</span>
                <span className="text-sm font-semibold text-danger">{(100 - selectedRegion.passRate).toFixed(0)}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* VS Divider */}
        <div className="flex items-center">
          <div className="bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-full w-10 h-10 flex items-center justify-center text-xs text-[var(--text-secondary)] font-medium">
            VS
          </div>
        </div>

        {/* FRANKIEONE Section */}
        <div className="space-y-3">
          {/* Header */}
          <div className="text-center border-b border-[var(--border-color)] pb-3">
            <div className="text-sm font-semibold text-primary uppercase tracking-wide">FrankieOne</div>
            <div className="text-xs text-[var(--text-muted)]">Cascading Verification</div>
          </div>

          {/* Flow diagram - Cascading */}
          <div className="bg-[var(--bg-tertiary)] rounded-lg p-5">
            {selectedRegion.stack.length === 0 ? (
              <EmptyState
                icon={GitBranch}
                title="No verification products"
                description="Add products to a region to see the verification flow"
              />
            ) : (
              <div className="space-y-0">
                {/* Customer node - aligned left */}
                <div className="flex">
                  <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 py-2 text-sm text-[var(--text-primary)] flex items-center gap-2">
                    <User size={16} />
                    Customer
                  </div>
                </div>

                {/* Cascade steps */}
                {steps.map((step, index) => (
                  <div key={index} style={{ marginLeft: `${index * 40}px` }}>
                    {/* Vertical connector line */}
                    <div className="flex">
                      <div className="w-0.5 h-6 bg-gray-600 ml-6" />
                    </div>

                    {/* Step card with match rate */}
                    <div className="flex items-center gap-4">
                      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-3 min-w-[160px]">
                        {/* Step header with number */}
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                            <span className="text-[var(--text-primary)] font-medium text-xs">{index + 1}</span>
                          </div>
                          <span className="text-xs text-primary font-medium">{getOrdinal(index + 1)} Pass KYC</span>
                        </div>
                        {/* Product name */}
                        <div className="font-medium text-[var(--text-primary)] text-sm mb-1 truncate">{step.name}</div>
                        {/* eKYC check tag */}
                        <div className="inline-block bg-[var(--bg-tertiary)] px-2 py-0.5 rounded text-[10px] text-[var(--text-muted)]">
                          eKYC check
                        </div>
                      </div>

                      {/* Arrow and match rate */}
                      <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                        <span>→</span>
                        <span>Match Rate {step.cumulativePass.toFixed(0)}%</span>
                      </div>
                    </div>

                    {/* Partial / No Match badge */}
                    <div className="flex ml-6 mt-1">
                      <div className="w-0.5 h-4 bg-gray-600" />
                    </div>
                    <div style={{ marginLeft: `${(index + 1) * 20}px` }} className="my-2">
                      <div className="inline-flex items-center gap-2 bg-danger/10 border border-danger/30 rounded px-3 py-1.5">
                        <span className="text-xs text-danger/80">Partial / No Match</span>
                        <span className="text-sm font-semibold text-danger">{step.remainingFailAfter.toFixed(0)}%</span>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Final Results - 40% No Match, 60% Match of combined width */}
                <div className="border-t border-[var(--border-color)] mt-6 pt-6">
                  <div className="flex justify-center">
                    <div className="flex gap-4">
                      {/* No Match box - 40% of combined */}
                      <div className="flex-[0.4] min-w-[120px] bg-danger/10 border border-danger/30 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-danger">{finalFailRate.toFixed(0)}%</div>
                        <div className="text-xs text-danger/80 mt-1">Partial /<br />No Match</div>
                      </div>

                      {/* Match box - 60% of combined */}
                      <div className="flex-[0.6] min-w-[180px] bg-success/10 border border-success/30 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-success">{finalPassRate.toFixed(0)}%</div>
                        <div className="text-xs text-success/80 mt-1">Match</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
